import { useQuery } from 'react-query'
import { WEB_URL, DEFAULT_AGOL_PORTAL_URL } from '../constant'
import { routes } from '../router'
import ApiClient, { INSTALINK_API_URL } from '../lib/api-client'
import getClientPortalUrl from '../lib/get-client-portal-url'
import { isValidUrl } from '../lib/parse-portal-url'
import { getClientRegionCode } from '../lib/get-default-region'

const AUTH_TIMEOUT = 3600 // Timeout for ArcGIS Identity token
const WHITELIST_PARAMS = ['domain', 'next', 'portalUrl', 'regionCode']

export function getRedirectParams (portalUrl) {
  const params = new URLSearchParams(window.location.search)
  const searchParams = new URLSearchParams()

  // NOTE: Only allow whitelisted search params.
  WHITELIST_PARAMS.forEach(key => {
    if (params.has(key)) {
      const value = params.get(key)
      if (value && value !== 'null') {
        searchParams.set(key, value)
      }
    }
  })

  // Append domain route for api server (match App Registration in ArcGIS)
  searchParams.set('domain', WEB_URL)

  // append route after login
  let next = searchParams.get('next') ?? '/'
  if (next === '/') {
    next = routes.dashboard()
  }

  searchParams.set('next', next)
  searchParams.set('portalUrl', portalUrl)

  // Include regionCode to domain for multi-region support
  const regionCode = getClientRegionCode()
  if (regionCode) {
    searchParams.set('regionCode', getClientRegionCode())
  }

  return searchParams
}

export function buildArcgisLoginUrl (params, portalUrl, clientId) {
  const data = window.btoa(params.toString())
  const redirectUri = `${WEB_URL}/auth?data=${data}`
  const apiRoot = portalUrl + '/sharing/rest/oauth2/authorize?response_type=code&expiration=20160'
  return `${apiRoot}&client_id=${clientId}&expiration=${AUTH_TIMEOUT}&redirect_uri=${redirectUri}`
}

async function fetchClientId (newPortalUrl, code, returnPath, handleResponse) {
  const portalUrl = getClientPortalUrl(newPortalUrl)
  const url = '/auth/client-id?portalUrl=' + portalUrl

  const data = await ApiClient.get(url, handleResponse)
  const redirectParams = getRedirectParams(portalUrl)
  if (code) {
    redirectParams.append('setup', code)
    redirectParams.append('returnPath', returnPath)
  }
  const loginUrl = buildArcgisLoginUrl(redirectParams, portalUrl, data.clientId)
  return loginUrl
}

export function useLoginUrlSetup (portalUrl, code, returnPath, isEnable, handleResponse) {
  return useQuery(
    ['clientId', portalUrl, code, returnPath],
    () => fetchClientId(portalUrl, code, returnPath, handleResponse),
    {
      enabled: isValidUrl(portalUrl) && isEnable,
      staleTime: (60 * 1000) // 1 minutes
    }
  )
}

function useLoginUrl (portalUrl) {
  return useQuery(
    ['clientId', portalUrl || DEFAULT_AGOL_PORTAL_URL],
    () => fetchClientId(portalUrl),
    {
      retry: 1,
      staleTime: (60 * 1000) // 1 minutes
    }
  )
}

export async function postAuthenticate (payload) {
  const url = `${INSTALINK_API_URL}/auth/auth-callback`
  return fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  })
    .then(response => response.json())
    .then(data => {
      return { data, error: null }
    })
    .catch(error => {
      return { data: null, error }
    })
}

export default useLoginUrl
